import { Mp3Encoder } from '@breezystack/lamejs';

/**
 * Download helpers for the music control's stem/track downloads (PLAT-10627 /
 * DE6). The stems manifest signs the track URLs with `Content-Disposition:
 * attachment` (PLAT-10627), so the browser downloads them natively and instantly
 * — we just navigate to the URL, never buffering the whole file through JS. The
 * AI dub track exists only as a composed in-memory `AudioBuffer`, so it is encoded
 * client-side — to a compact MP3 (PLAT-10661) or a lossless WAV (PLAT-10715),
 * whichever the user picks.
 */

/** Save a Blob to disk under `filename`. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has taken effect.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Fetch a signed track URL and save it under `filename`. The signed URLs are
 * cross-origin, so an anchor's `download` attribute is ignored on a direct
 * navigation (the server disposition wins) — buffering through a same-origin
 * Blob is the only way to control the filename (PLAT-10715). Rejects if the
 * fetch fails; callers log + swallow.
 */
export async function downloadRemote(
  url: string,
  filename: string,
): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status} ${res.statusText}`);
  }
  saveBlob(await res.blob(), filename);
}

/** Human-readable file size — MB at ≥1 MB, else KB (min 1 KB). */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Encode an `AudioBuffer` to a 16-bit PCM WAV Blob (channels interleaved). Used
 * to download the composed AI dub track, which has no server-side file.
 */
export function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = numFrames * blockAlign;

  const arrayBuffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i += 1)
      view.setUint8(offset + i, s.charCodeAt(i));
  };

  // RIFF/WAVE header.
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true); // byte rate
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 8 * bytesPerSample, true); // bits per sample
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  // Interleave channels, converting float [-1,1] to int16.
  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c += 1)
    channels.push(buffer.getChannelData(c));
  let offset = 44;
  for (let i = 0; i < numFrames; i += 1) {
    for (let c = 0; c < numChannels; c += 1) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true,
      );
      offset += 2;
    }
  }
  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

/** Encode + save an `AudioBuffer` as `filename` (WAV). */
export function downloadAudioBuffer(
  buffer: AudioBuffer,
  filename: string,
): void {
  saveBlob(audioBufferToWavBlob(buffer), filename);
}

/** Clamp + convert a float [-1,1] channel to 16-bit PCM for the MP3 encoder. */
function floatChannelToInt16(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/**
 * Encode an `AudioBuffer` to an MP3 Blob client-side (PLAT-10661) — browsers
 * have no built-in MP3 encoder, so we use lamejs. The composed AI dub is mono,
 * but stereo is handled for safety. Encoding runs in 1152-sample frames (the MP3
 * frame size) and yields to the event loop periodically so a long track doesn't
 * freeze the UI. `kbps` defaults to 128 (ample for mono voice).
 */
export async function audioBufferToMp3Blob(
  buffer: AudioBuffer,
  kbps = 128,
): Promise<Blob> {
  const channels = buffer.numberOfChannels > 1 ? 2 : 1;
  const encoder = new Mp3Encoder(channels, buffer.sampleRate, kbps);
  const left = floatChannelToInt16(buffer.getChannelData(0));
  const right =
    channels > 1 ? floatChannelToInt16(buffer.getChannelData(1)) : undefined;

  const FRAME = 1152;
  const parts: Uint8Array[] = [];
  let framesSinceYield = 0;
  for (let i = 0; i < left.length; i += FRAME) {
    const l = left.subarray(i, i + FRAME);
    const chunk = right
      ? encoder.encodeBuffer(l, right.subarray(i, i + FRAME))
      : encoder.encodeBuffer(l);
    if (chunk.length > 0) parts.push(chunk);
    framesSinceYield += 1;
    if (framesSinceYield >= 400) {
      framesSinceYield = 0;
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }
  const end = encoder.flush();
  if (end.length > 0) parts.push(end);
  return new Blob(parts as BlobPart[], { type: 'audio/mpeg' });
}

/** Encode + save an `AudioBuffer` as `filename` (MP3). */
export async function downloadAudioBufferAsMp3(
  buffer: AudioBuffer,
  filename: string,
): Promise<void> {
  saveBlob(await audioBufferToMp3Blob(buffer), filename);
}
